// ============================================================
// Tricorder Hub — operator war room for client implementations.
//
// Aggregates all projects from sq_projects with joined health
// metadata. No mock data — renders real data or polished empty
// states. Entry point: /square/tricorder
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SectionHeader } from '@square/ui'
import EmptyState from '../../components/EmptyState'
import { api, isAuthError, type TricorderClient } from '../../api/client'
import { SHELL_TOKENS, statusAccent, healthAccent, timeAgo } from '../../shell/styles'

export default function TricorderHub() {
  const navigate = useNavigate()
  const [clients, setClients] = useState<TricorderClient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.tricorder.clients()
      setClients(r.clients)
      setError(null)
    } catch (err: any) {
      if (!isAuthError(err)) setError(err.message || 'Failed to load clients')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const active = clients.filter((c) => c.status !== 'archived')
  const archived = clients.filter((c) => c.status === 'archived')

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <SectionHeader
        as="page"
        accent={SHELL_TOKENS.orange}
        kicker="TRICORDER · CLIENT MANAGEMENT"
        title="Operator Hub"
        description="Governed client implementations · real data only"
        style={{ marginBottom: 28 }}
      />

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 24 }}>
        <KPI label="Clients" value={clients.length} sub="total portfolio" />
        <KPI label="Active" value={active.length} sub="in progress" accent={SHELL_TOKENS.green} />
        <KPI
          label="Health"
          value={clients.filter((c) => (c.health_score ?? c.health?.health_score ?? 0) >= 70).length + '/' + clients.length}
          sub="above threshold"
        />
        <KPI
          label="Open threads"
          value={clients.reduce((s, c) => s + c.open_threads, 0)}
          sub="awaiting response"
          accent={clients.reduce((s, c) => s + c.open_threads, 0) > 0 ? SHELL_TOKENS.amber : undefined}
        />
      </div>

      {/* Client list */}
      {loading ? (
        <EmptyState title="Loading clients" body="Pulling portfolio from sq_projects." />
      ) : error ? (
        <EmptyState title="Failed to load" body={error} />
      ) : clients.length === 0 ? (
        <EmptyState
          title="No clients yet"
          body="Create projects in sq_projects to populate the Tricorder Hub. Each project becomes a client implementation."
        />
      ) : (
        <>
          <SectionLabel>ACTIVE IMPLEMENTATIONS</SectionLabel>
          {active.length === 0 ? (
            <EmptyState title="No active clients" body="All implementations are archived." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {active.map((c) => (
                <ClientCard
                  key={c.id}
                  client={c}
                  onOpen={() => navigate(`/square/tricorder/${c.id}`)}
                  onWorkspace={() => navigate(`/square/projects/${c.id}`)}
                />
              ))}
            </div>
          )}

          {archived.length > 0 && (
            <>
              <SectionLabel style={{ marginTop: 24 }}>ARCHIVED</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {archived.map((c) => (
                  <ClientCard
                    key={c.id}
                    client={c}
                    onOpen={() => navigate(`/square/tricorder/${c.id}`)}
                    onWorkspace={() => navigate(`/square/projects/${c.id}`)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

// ----------------------------------------------------------------

function ClientCard({
  client: c,
  onOpen,
  onWorkspace,
}: {
  client: TricorderClient
  onOpen: () => void
  onWorkspace: () => void
}) {
  const accent = statusAccent(c.status)
  const score = c.health_score ?? c.health?.health_score ?? null

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', borderRadius: 12,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
        transition: 'border-color 0.15s',
      }}
    >
      {/* Glyph */}
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `${accent}14`, border: `1px solid ${accent}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, color: accent, fontFamily: 'var(--sc-font-mono, var(--font-mono))', flexShrink: 0,
      }}>
        ◈
      </div>

      {/* Identity */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: SHELL_TOKENS.ink, letterSpacing: 0 }}>
            {c.client_name || c.name}
          </span>
          <span style={{
            padding: '3px 9px', borderRadius: 6, fontSize: 10,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))', fontWeight: 700,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            background: `${accent}1a`, color: accent, border: `1px solid ${accent}33`,
          }}>
            {c.status}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 14,
            fontSize: 11,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            color: SHELL_TOKENS.inkFaint,
            letterSpacing: '0.04em',
          }}
        >
          {c.domain && <span>{c.domain}</span>}
          <span>updated {timeAgo(c.updated_at)}</span>
          {c.update_count > 0 && <span>{c.update_count} updates</span>}
          {c.open_threads > 0 && <span style={{ color: SHELL_TOKENS.gold }}>{c.open_threads} open threads</span>}
        </div>
      </div>

      {/* Health */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 54 }}>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            color: score !== null ? healthAccent(score) : SHELL_TOKENS.inkFaint,
            letterSpacing: 0,
          }}
        >
          {score !== null ? `${score}%` : '—'}
        </span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            color: SHELL_TOKENS.inkFaint,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          HEALTH
        </span>
      </div>

      {/* Repo indicators */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <RepoDot provider="GH" linked={!!c.github_repo_url} />
        <RepoDot provider="GL" linked={!!c.gitlab_url} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <ActionBtn label="Open" onClick={onOpen} />
        <ActionBtn label="Workspace" onClick={onWorkspace} />
      </div>
    </div>
  )
}

function RepoDot({ provider, linked }: { provider: string; linked: boolean }) {
  // Linked = real integration, so the green tint is truthful. Unlinked stays
  // on the purple substrate so it never pretends to be a live connection.
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        background: linked ? `${SHELL_TOKENS.green}14` : SHELL_TOKENS.surfaceAlt,
        border: `1px solid ${linked ? SHELL_TOKENS.green + '33' : SHELL_TOKENS.border}`,
        boxShadow: linked ? `0 0 6px ${SHELL_TOKENS.green}29` : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        fontFamily: 'var(--sc-font-mono, var(--font-mono))',
        color: linked ? SHELL_TOKENS.green : SHELL_TOKENS.inkFaint,
        letterSpacing: '0.08em',
      }}
    >
      {provider}
    </div>
  )
}

function ActionBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '7px 12px',
        borderRadius: 6,
        border: `1px solid ${SHELL_TOKENS.border}`,
        background: 'transparent',
        color: SHELL_TOKENS.inkDim,
        fontSize: 10,
        fontWeight: 700,
        fontFamily: 'var(--sc-font-mono, var(--font-mono))',
        cursor: 'pointer',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        transition: 'background 0.25s var(--sc-ease), color 0.25s',
      }}
    >
      {label}
    </button>
  )
}

function KPI({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string | number
  sub: string
  accent?: string
}) {
  const tinted = !!accent
  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: 12,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${tinted ? accent + '26' : SHELL_TOKENS.border}`,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
          color: SHELL_TOKENS.inkFaint,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: accent || SHELL_TOKENS.ink,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
          letterSpacing: 0,
          marginBottom: 6,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
          color: SHELL_TOKENS.inkFaint,
          letterSpacing: '0.04em',
        }}
      >
        {sub}
      </div>
    </div>
  )
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 16,
          height: 2,
          background: SHELL_TOKENS.orange,
          opacity: 0.4,
          borderRadius: 1,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: SHELL_TOKENS.orange,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
        }}
      >
        {children}
      </span>
    </div>
  )
}
